import { Space, Tag, Typography, Row, Col, Button, Popconfirm } from 'antd';
import React, { useState } from 'react';
import moment from 'moment';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { PricingAPI } from '../../../../../controller/PricingAPI';
import { checklistAllDone, checklistLabel, dueStatus, formatStartTime, isOccurrence } from '../todoHelpers';
import TodoChecklist from './TodoChecklist';

// 早期資料可能沒有 ignoredtime / completedtime，直接 new Date(null) 會得到 epoch，
// 畫面上會顯示成 1970/1/1；一律先擋掉空值與無效日期
const formatDateTime = (value) => {
    if (!value) {
        return '未記錄';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '未記錄' : date.toLocaleString();
};

function TodoItem({ item, onCompleted, onIgnored, onUndo, onNoteEdit = null, onEdit, onClaim, onRelease, onStart = null, loading, onItemsChanged = null, defaultExpanded = false, highlighted = false, rowRef = null }) {
    const [notes, setNotes] = React.useState(item.notes || []);
    const [expanded, setExpanded] = useState(defaultExpanded);

    // 頁面已開著時再點另一封信 (只換 hash 不重掛)，新命中的那一列要跟著展開。
    // 只處理 false -> true，不做成完全受控，使用者自己的展開收合才不會被蓋掉
    React.useEffect(() => {
        if (defaultExpanded) {
            setExpanded(true);
        }
    }, [defaultExpanded]);

    React.useEffect(() => {
        setNotes(item.notes || []);
    }, [item]);

    // agenda 不內嵌 description (佔回應七成)，只給 has_description；展開時才用 show 取。
    // 虛擬項目 (沒有 id) 與 index 來的列 (description 已在 payload) 都不取
    const descriptionEmbedded = Object.prototype.hasOwnProperty.call(item, 'description');
    const needsFetch = !!item.id && !descriptionEmbedded && item.has_description === true;
    // status: idle | loading | done | missing (404) | failed
    const [lazy, setLazy] = useState({ status: 'idle', description: '' });
    const mounted = React.useRef(true);
    const generation = React.useRef(0);
    const lastUpdatedAt = React.useRef(item.updated_at);

    React.useEffect(() => {
        mounted.current = true;
        return () => {
            // 展開後完成那一筆，父層重抓，這一列會卸載但請求還在飛
            mounted.current = false;
        };
    }, []);

    // 這一列被編輯過 (updated_at 變了) 就丟掉取回的描述，否則會一直顯示舊的
    React.useEffect(() => {
        if (lastUpdatedAt.current !== item.updated_at) {
            lastUpdatedAt.current = item.updated_at;
            generation.current += 1;
            setLazy({ status: 'idle', description: '' });
        }
    }, [item.updated_at]);

    React.useEffect(() => {
        if (!expanded || !needsFetch || lazy.status !== 'idle') {
            return;
        }
        generation.current += 1;
        const mine = generation.current;
        setLazy({ status: 'loading', description: '' });
        PricingAPI.todos.show(item.id).then((response) => {
            if (mounted.current && mine === generation.current) {
                setLazy({ status: 'done', description: response.data.description || '' });
            }
        }).catch((error) => {
            if (mounted.current && mine === generation.current) {
                setLazy({ status: error?.response?.status === 404 ? 'missing' : 'failed', description: '' });
            }
        });
    }, [expanded, needsFetch, lazy.status, item.id]);

    const getStatusTag = (state) => {
        switch (state) {
            case 'new':
                return <Tag color="red">新待辦</Tag>
            case 'ignored':
                return <Tag color="gray">已忽略</Tag>
            case 'completed':
                return <Tag color="green">已完成</Tag>
            default:
                break;
        }
        return <Tag color="gray">未知</Tag>
    }

    const can = item.can || {};

    // 展開區要顯示的描述，與取不到時的替代文字
    const description = descriptionEmbedded ? item.description : lazy.description;
    let descriptionNotice = null;
    if (needsFetch && (lazy.status === 'idle' || lazy.status === 'loading')) {
        descriptionNotice = '載入中...';
    } else if (needsFetch && lazy.status === 'missing') {
        descriptionNotice = '這筆待辦已不存在或你沒有權限查看';
    } else if (needsFetch && lazy.status === 'failed') {
        descriptionNotice = '描述載入失敗，收合後重新展開可再試一次';
    } else if (!descriptionEmbedded && !needsFetch) {
        descriptionNotice = '沒有描述';
    }

    // 虛擬項目還沒落地，沒有 id 可以操作 —— 它只有一顆「開始」
    const occurrence = isOccurrence(item);

    const getDateInfo = () => {
        // 虛擬項目不是資料列，沒有建立日期；它所在的分組標題已經寫了日期
        if (occurrence) {
            return null;
        }
        if (item.state === "new") {
            return <Typography.Text type="secondary">建立日期 {formatDateTime(item.created_at)}</Typography.Text>
        } else if (item.state === "ignored") {
            return <Typography.Text type="secondary">忽略日期 {formatDateTime(item.ignoredtime)} 由 {item.processuser ? item.processuser.name : '未知'}</Typography.Text>
        } else if (item.state === "completed") {
            return <Typography.Text type="secondary">完成日期 {formatDateTime(item.completedtime)} 由 {item.processuser ? item.processuser.name : '未知'} 處理</Typography.Text>
        }
        return null;
    }

    const getDueTag = () => {
        if (!item.due_date) {
            return null;
        }
        const status = dueStatus(item.due_date, item.state);
        const color = status === 'overdue' ? 'red' : status === 'soon' ? 'orange' : undefined;
        const prefix = status === 'overdue' ? '已逾期 ' : '到期 ';
        return <Tag color={color} style={{ whiteSpace: 'nowrap' }}>{prefix}{item.due_date}</Tag>;
    };

    const getDepartmentTag = () => {
        if (!item.department) {
            return null;
        }
        return <Tag color="blue" style={{ whiteSpace: 'nowrap' }}>{item.department.name}</Tag>;
    };

    const getStartTimeTag = () => {
        const time = formatStartTime(item.start_time);
        if (!time) {
            return null;
        }
        // 其他清單沒有日期分組做脈絡，孤立的 09:00 會被讀成「今天 9 點」，所以有日期就帶 MM/DD
        const date = item.start_date ? moment(item.start_date, 'YYYY-MM-DD', true) : null;
        const label = date && date.isValid() ? `${date.format('MM/DD')} ${time}` : time;
        return <Tag color="geekblue" style={{ whiteSpace: 'nowrap' }}>{label}</Tag>;
    };

    const getChecklistTag = () => {
        const text = checklistLabel(item.checklist);
        if (!text) {
            return null;
        }
        return (
            <Tag
                color={checklistAllDone(item.checklist) ? 'green' : undefined}
                style={{ whiteSpace: 'nowrap' }}
            >
                {text}
            </Tag>
        );
    };

    const getRoutineTag = () => {
        if (!occurrence) {
            return null;
        }
        // 讓人分得出「還沒開始的例行工作」與「已經在手上的工作」
        return <Tag color="purple" style={{ whiteSpace: 'nowrap' }}>例行</Tag>;
    };

    const toggleExpand = (e) => {
        e.stopPropagation();
        // 取失敗的列，收合再展開時給它重試的機會
        if (lazy.status === 'failed') {
            setLazy({ status: 'idle', description: '' });
        }
        setExpanded(!expanded);
    };

    return (
        <div 
            ref={rowRef}
            style={{ 
                // 信件連結指到的那一列：在一百多列裡光是展開不夠顯眼，加上 antd 主色的底與框
                backgroundColor: highlighted ? '#e6f7ff' : '#fafafa',
                border: highlighted ? '1px solid #1890ff' : '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '12px', 
                width: '100%',
                cursor: 'pointer'
            }} 
            key={item.id}
            onClick={toggleExpand}
        >
            <Row gutter={[10, 10]} align="middle">
                <Col flex="none" style={{ display: 'flex', alignItems: 'center' }}>
                    {expanded ? <DownOutlined /> : <RightOutlined />}
                </Col>
                <Col flex="none">
                    {getStatusTag(item.state)}
                </Col>
                <Col flex="auto">
                    <Space size={8} wrap>
                        {getRoutineTag()}
                        {getStartTimeTag()}
                        <Typography.Text strong>{item.subject}</Typography.Text>
                        {getChecklistTag()}
                        {getDueTag()}
                        {getDepartmentTag()}
                        <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                            負責人 {item.assignee ? item.assignee.name : '未指派'}
                        </Typography.Text>
                    </Space>
                </Col>
                <Col flex="none" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {getDateInfo()}
                    {!occurrence && (<>
                    <Popconfirm
                        key="ignore" 
                        title="確定要略過嗎?"
                        disabled={item.state !== 'new' || !can.change_state || loading}
                        onConfirm={(e) => {
                            e.stopPropagation();
                            onIgnored(item.id);
                        }}
                    >
                        <Button 
                            type="text" 
                            size="small" 
                            disabled={item.state !== 'new' || !can.change_state || loading}
                            onClick={(e) => e.stopPropagation()}
                        >
                            略過
                        </Button>
                    </Popconfirm>
                    <Button 
                        type="text" 
                        size="small" 
                        disabled={item.state !== 'new' || !can.change_state || loading}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCompleted(item.id);
                        }}
                    >
                        完成
                    </Button>
                    {item.state !== 'new' && can.change_state && (
                        <Button 
                            type="text" 
                            size="small" 
                            onClick={(e) => {
                                e.stopPropagation();
                                onUndo(item.id);
                            }}
                        >
                            還原
                        </Button>
                    )}
                    </>)}
                    {can.claim && (
                        <Button
                            type="link"
                            size="small"
                            disabled={loading}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClaim(item.id);
                            }}
                        >
                            認領
                        </Button>
                    )}
                    {can.release && item.assignee && (
                        <Popconfirm
                            title="放回後這件工作就沒有負責人，任何人都能接手"
                            disabled={loading}
                            onConfirm={(e) => {
                                e.stopPropagation();
                                onRelease(item.id);
                            }}
                        >
                            <Button type="text" size="small" disabled={loading} onClick={(e) => e.stopPropagation()}>
                                放回
                            </Button>
                        </Popconfirm>
                    )}
                    {!occurrence && (can.update || can.schedule) && (
                        <Button
                            type="text"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(item);
                            }}
                        >
                            {can.update ? '編輯' : '排程'}
                        </Button>
                    )}
                    {occurrence && (
                        <Button
                            type="primary"
                            size="small"
                            disabled={loading}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onStart) {
                                    onStart(item);
                                }
                            }}
                        >
                            開始
                        </Button>
                    )}
                </Col>
            </Row>
            
            {expanded && (
                <div data-testid={`todo-expanded-${item.id}`} style={{ marginTop: '12px', paddingLeft: '24px' }}>
                    {descriptionNotice ? (
                        <Typography.Text type="secondary" data-testid={`todo-description-notice-${item.id}`}>{descriptionNotice}</Typography.Text>
                    ) : item.source === 'manual' || item.source === 'routine' ? (
                        <div style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                            {description || <Typography.Text type="secondary">沒有描述</Typography.Text>}
                        </div>
                    ) : (
                        <div
                            style={{ maxHeight: 400, overflow: 'auto' }}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    )}
                    {item.creator && (
                        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                            由 {item.creator.name} 建立
                        </Typography.Paragraph>
                    )}

                    <TodoChecklist
                        todo={item}
                        canTick={!!can.change_state}
                        onChanged={onItemsChanged}
                    />

                    {notes.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                            <Typography.Title level={5}>工作摘要</Typography.Title>
                            {notes.map((note) => (
                                <div key={note.id} style={{ marginBottom: '8px' }}>
                                    <Space>
                                        <Typography.Text type="secondary">{formatDateTime(note.created_at)} {note.processuser ? note.processuser.name : '未知'}: </Typography.Text>
                                        <Typography.Text>{note.note}</Typography.Text>
                                        {note.can_edit && onNoteEdit && (
                                            <Typography.Link onClick={(e) => {
                                                e.stopPropagation();
                                                onNoteEdit(item.id, note.id);
                                            }}>編輯</Typography.Link>
                                        )}
                                    </Space>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TodoItem;